import { ChangeDetectionStrategy, Component, inject, Injector, OnInit, Signal } from '@angular/core';
import {
  DocumentRequestsStore,
  DocumentRequestsStoreState
} from '@modules/core/component-stores/document-requests.store';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  ContainerState,
  PaginationRange,
  TiimeButtonModule,
  TiimeContainerModule,
  TiimeEmptyStateModule,
  TiimeIconModule,
  TiimePaginatorModule,
  TiimeSearchBarModule,
  TiimeSnackbarService,
  TiimeTableModule
} from 'tiime-material';
import { Subscription } from 'rxjs';
import { shareReplay1 } from '@helpers/rxjs.helper';
import { RefreshButtonComponent } from '@shared-components/refresh-button/refresh-button.component';
import { DocumentRequestComponent } from '@shared-components/document-requests-panel/components/document-request/document-request.component';
import { DocumentRequest } from '@models/document-request';
import { distinctUntilChanged, filter, startWith, tap } from 'rxjs/operators';
import { DocumentRequestFormComponent } from '@shared-forms/document-request-form/document-request-form.component';
import { DocumentRequestForm } from '@forms/document-request-form';
import { DocumentRequestModuleType } from '@enums/document-request-module-type.enum';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
  firstOpenedAccountingPeriodSelector,
  selectedAccountingPeriodInstanceSelector
} from '@modules/company/company-core/store/accounting-periods/accounting-periods-selector';
import { AccountingPeriod } from '@models/accounting-period';
import { MarkFormAsTouchedIfInvalid } from 'tiime-expert-utils';
import { DocumentRequestDetailComponent } from '@shared-components/document-requests-panel/components/document-request-detail/document-request-detail.component';
import { FileSaverData, FileService } from '@services/file.service';
import { DocumentRequestFiltersButtonComponent } from '@shared-components/document-requests-panel/components/document-request-filters-button/document-request-filters-button.component';
import { DocumentRequestFiltersForm } from '@forms/document-request-filters-form';
import { FileHelper } from '@helpers/file.helper';
import {
  extendedAcceptedFileTypes,
  UPLOAD_FILE_CONFIG
} from '@shared-overlays/upload-file-overlay/upload-file-config.constant';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-document-requests-panel',
  standalone: true,
  imports: [
    TiimeIconModule,
    TiimeTableModule,
    TiimeButtonModule,
    TiimeContainerModule,
    TiimeSearchBarModule,
    TiimePaginatorModule,
    RefreshButtonComponent,
    DocumentRequestComponent,
    DocumentRequestFormComponent,
    DocumentRequestDetailComponent,
    DocumentRequestFiltersButtonComponent,
    TiimeEmptyStateModule
  ],
  templateUrl: './document-requests-panel.component.html',
  styleUrl: './document-requests-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentRequestsPanelComponent implements OnInit {
  readonly form = new DocumentRequestForm();
  readonly filtersForm = new DocumentRequestFiltersForm();

  private readonly subscription = new Subscription();
  private readonly injector = inject(Injector);
  private readonly documentRequestsStore = inject(DocumentRequestsStore);
  private readonly store = inject(Store);
  private readonly snackbarService = inject(TiimeSnackbarService);
  private readonly fileService = inject(FileService);
  private templateFile: File | null = null;
  readonly acceptedTypes = extendedAcceptedFileTypes;
  readonly maximumSize = UPLOAD_FILE_CONFIG.maximumSize;
  readonly selectedAccountingPeriod: Signal<AccountingPeriod> = this.store.selectSignal(
    selectedAccountingPeriodInstanceSelector
  );
  readonly firstOpenedAccountingPeriod: Signal<AccountingPeriod> = this.store.selectSignal(
    firstOpenedAccountingPeriodSelector
  );
  readonly accountingPeriods: Signal<AccountingPeriod[]> = toSignal(
    this.documentRequestsStore.accountingPeriods$.pipe(shareReplay1())
  );
  readonly state: Signal<DocumentRequestsStoreState> = this.documentRequestsStore.state;
  readonly disabledActions: Signal<boolean> = toSignal(
    this.documentRequestsStore.disabledActions$.pipe(shareReplay1())
  );
  readonly ContainerState = ContainerState;

  ngOnInit(): void {
    this.observeOpen();
    this.observeFilters();
    this.observeAccountingPeriods();
    this.observeFormModuleType();
  }

  emitClosePanel(): void {
    this.documentRequestsStore.setOpened(false);
  }

  applySearch(filterValue: string): void {
    this.documentRequestsStore.setSearch(filterValue);
  }

  applyRange(range: PaginationRange): void {
    this.documentRequestsStore.setPagination(range);
  }

  refreshList(): void {
    this.documentRequestsStore.forceRefresh();
  }

  createDocumentRequestMode(): void {
    this.documentRequestsStore.setMode('Creation');
  }

  listDocumentRequestMode(): void {
    this.documentRequestsStore.setMode('List');
  }

  detailsDocumentRequestMode(request: DocumentRequest): void {
    this.documentRequestsStore.setSelected(request);
    this.documentRequestsStore.setMode('Detail');
  }

  @MarkFormAsTouchedIfInvalid('form')
  requestDocument(): void {
    this.documentRequestsStore
      .createDocumentRequest(this.form.toDocumentRequest(), this.templateFile)
      .pipe(
        tap(() => {
          this.snackbarService.openSuccess('La demande de document a bien été créée');
          this.documentRequestsStore.forceRefresh();
          this.documentRequestsStore.setMode('List');
        })
      )
      .subscribe();
  }

  openOrCloseDocumentRequest(request: DocumentRequest): void {
    const close = request.closedAt === null;
    const action = close ? 'clôturée' : 'déclôturée';

    this.documentRequestsStore
      .openOrCloseDocumentRequest(request, { close })
      .pipe(
        tap(() => {
          this.snackbarService.openSuccess(`La demande de document a bien été ${action}`);
        })
      )
      .subscribe();
  }

  downloadDocumentRequestTemplateFile(moduleType: DocumentRequestModuleType): void {
    const template = this.state().templates.find(t => t.moduleType === moduleType);

    this.documentRequestsStore
      .downloadDocumentRequestTemplateFile(template)
      .pipe(tap((fileSaverData: FileSaverData) => this.fileService.save(fileSaverData)))
      .subscribe();
  }

  downloadCustomerDocument(id: number): void {
    this.documentRequestsStore.downloadCustomerDocument(id).subscribe();
  }

  openCustomerDocumentInNewTab(id: number): void {
    this.documentRequestsStore.openCustomerDocumentInNewTab(id);
  }

  onFileSelected(file: File): void {
    if (!FileHelper.isValid(file, this.acceptedTypes, this.maximumSize)) {
      this.snackbarService.openError(FileHelper.getInvalidFileErrorMessage());
      return;
    }
    this.form.file.setValue({ name: file.name, uuid: null });
    this.templateFile = file;
  }

  onFileDeleted(): void {
    this.form.file.setValue(null);
    this.templateFile = null;
  }

  private observeFilters(): void {
    this.subscription.add(
      this.filtersForm.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap(() => this.documentRequestsStore.setFilters(this.filtersForm.toDocumentRequestFilters()))
        )
        .subscribe()
    );
  }

  private initDocumentRequestsStore(): void {
    this.documentRequestsStore.init();
  }

  private observeOpen(): void {
    this.subscription.add(
      this.documentRequestsStore.opened$
        .pipe(
          distinctUntilChanged(),
          filter((opened: boolean) => opened),
          tap(() => {
            this.initDocumentRequestsStore();
            if (this.state().mode === 'Creation') {
              this.form.patchValue({ moduleType: this.state().moduleType });
            }
          })
        )
        .subscribe()
    );
  }

  private observeAccountingPeriods(): void {
    this.subscription.add(
      this.store
        .select(selectedAccountingPeriodInstanceSelector)
        .pipe(tap(accountingPeriod => this.filtersForm.patchValue({ accountingPeriod })))
        .subscribe()
    );

    this.subscription.add(
      toObservable(this.firstOpenedAccountingPeriod, { injector: this.injector })
        .pipe(tap(accountingPeriod => this.form.patchValue({ accountingPeriodId: accountingPeriod.id })))
        .subscribe()
    );
  }

  private observeFormModuleType(): void {
    this.subscription.add(
      this.form.controls.moduleType.valueChanges
        .pipe(
          startWith(this.form.controls.moduleType.value),
          distinctUntilChanged(),
          tap(moduleType => {
            const template = this.state().templates.find(t => t.moduleType === moduleType);
            this.form.setValuesFromTemplate(template);
          }),
          tap(value => {
            if (value === DocumentRequestModuleType.CUSTOM) {
              this.form.enableInputs();
            } else {
              this.form.disableInputs();
            }
          })
        )
        .subscribe()
    );
  }
}
